import { defineStore } from "pinia";
import type { SupplementFormula } from "@/types/apiTypes";
import snakecaseKeys from "snakecase-keys";
import { axiosInstance } from "@/utils/api/axios_instance";
import { API } from "@/utils/api/api_paths";
import { useMainStore } from "@/stores/main";
import { useQuestionsStore } from "@/stores/questions";
import { getAnswersForRecommendation } from "@/utils/serializeAnswers";
import { getAnswersForOrderSubmit } from "@/utils/serializeAnswers";
import camelcaseKeys from "camelcase-keys";
import { LocalStorageKeys } from "@/utils/constants";
import { computed } from "vue";
import { StorageSerializers, useStorage } from "@vueuse/core";

export const useSupplementsStore = defineStore("supplementsStore", () => {
  const supplementsFormula = useStorage<SupplementFormula | null>(LocalStorageKeys.formula, null, undefined, {
    serializer: StorageSerializers.object,
  });

  const formulaDraftUuid = computed<string>(() => {
    return supplementsFormula.value?.formulaDraftUuid || "";
  });

  const hasQuestionnaireAnswersChanged = computed<boolean>(() => {
    const questionsStore = useQuestionsStore();

    const localStorageQuestionnaireAnswers = JSON.parse(localStorage.getItem(LocalStorageKeys.answer) || "");
    const questionnaireAnswers = questionsStore.allAnswers;
    return JSON.stringify(questionnaireAnswers) !== JSON.stringify(localStorageQuestionnaireAnswers);
  });

  const fetchSupplementsFormula = async (): Promise<boolean> => {
    if (!!formulaDraftUuid.value && !hasQuestionnaireAnswersChanged.value) return false;

    const mainStore = useMainStore();
    const questionsStore = useQuestionsStore();

    const profile = {
      ...questionsStore.profileQuestions,
      countryCode: mainStore.ipLocCountry.code,
    };

    try {
      const res = await axiosInstance.post(
        API.FORMULA_RECOMMENDATIONS,
        snakecaseKeys({
          answerKeys: getAnswersForRecommendation(),
          email: questionsStore.lifestyleStore.email,
          fullName: questionsStore.lifestyleStore.fullName,
          nameOnJar: questionsStore.lifestyleStore.fullName,
          bioniqGoQuestionnaire: getAnswersForOrderSubmit() as { questionKey: string; answerKeys: string[] }[],
          profile,
        })
      );
      const data: SupplementFormula = camelcaseKeys(res.data, { deep: true });
      setFormula(data);
      return false;
    } catch (err) {
      console.error(`Error fetching the supplements formula, ${err}`);
      return true;
    }
  };

  const setFormula = (data: SupplementFormula) => {
    supplementsFormula.value = data;
  };

  return {
    supplementsFormula,
    formulaDraftUuid,
    hasQuestionnaireAnswersChanged,
    fetchSupplementsFormula,
    setFormula,
  };
});
